import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page, PageHeader } from '../components/Page';
import { PCard } from '../components/PCard';
import { StatusPill } from '../components/StatusPill';
import { Async } from '../components/Async';
import { IcReceipt } from '../components/icons';
import { useFetch } from '../lib/useFetch';
import { useAuth } from '../lib/auth';
import { getFedexInvoices, uploadFedexInvoice, ApiError, type FedexInvoiceSummary } from '../lib/api';
import { fmtCents, fmtNum, fmtUtcDate } from '../lib/format';

export const FedexBillsPage = () => {
  const { can } = useAuth();
  const nav = useNavigate();
  const invoices = useFetch(getFedexInvoices);
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await uploadFedexInvoice(file);
      invoices.reload();
      // Take the user to the bill; if it was a duplicate, flag it so the detail
      // page can say "already uploaded" instead of pretending it's new.
      nav(`/fedex/${r.invoice.id}`, r.alreadyUploaded ? { state: { reupload: true } } : undefined);
    } catch (err) {
      setMsg({ tone: 'err', text: err instanceof ApiError ? err.message : 'Upload failed' });
      setBusy(false);
    }
  }

  return (
    <Page>
      <PageHeader
        title="FedEx Bills"
        sub="Upload FedEx invoices · parse shipments · reconcile real cost against pharmacy shipments per brand"
        actions={
          can('billing:run') ? (
            <>
              <input ref={fileRef} type="file" accept="application/pdf,.pdf" onChange={onFile} style={{ display: 'none' }} />
              <button className="btn btn--primary" onClick={() => fileRef.current?.click()} disabled={busy}>
                {busy ? 'Uploading…' : 'Upload bill (PDF)'}
              </button>
            </>
          ) : undefined
        }
      />

      {msg && (
        <div
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            fontSize: 13,
            background: msg.tone === 'ok' ? 'rgba(16,185,129,.08)' : 'rgba(239,68,68,.08)',
            color: msg.tone === 'ok' ? 'var(--status-success-ink,#047857)' : 'var(--status-error-ink,#b91c1c)',
          }}
        >
          {msg.text}
        </div>
      )}

      <PCard style={{ padding: 16, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div className="p-stat__icon p-stat__icon--in-transit">
          <IcReceipt size={18} />
        </div>
        <div style={{ fontSize: 13, color: 'var(--p-muted-fg)' }}>
          Upload the weekly FedEx invoice PDF. Each shipment is matched by tracking number to a pharmacy
          shipment (<strong style={{ color: 'var(--p-foreground)' }}>pharmacy-terminal</strong>, then pharmacyV2)
          to tag the real cost per <strong style={{ color: 'var(--p-foreground)' }}>brand</strong>. Where the brand
          is a billing client, we compare FedEx's cost to what we charged. This is an expense/audit record — it
          never bills a client, and no recipient PHI is stored.
        </div>
      </PCard>

      <div>
        <div className="section-title">Uploaded bills</div>
        <Async state={invoices} isEmpty={(d) => d.invoices.length === 0} emptyLabel="No bills uploaded yet.">
          {({ invoices: rows }) => (
            <PCard>
              <table className="p-table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Date</th>
                    <th style={{ textAlign: 'right' }}>Shipments</th>
                    <th style={{ textAlign: 'right' }}>Matched</th>
                    <th style={{ textAlign: 'right' }}>Express total</th>
                    <th style={{ textAlign: 'right' }}>Grand total</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <FedexRow key={r.id} r={r} onOpen={() => nav(`/fedex/${r.id}`)} />
                  ))}
                </tbody>
              </table>
            </PCard>
          )}
        </Async>
      </div>
    </Page>
  );
};

function FedexRow({ r, onOpen }: { r: FedexInvoiceSummary; onOpen: () => void }) {
  const matchPct = r.shipmentCount ? Math.round((r.matchedCount / r.shipmentCount) * 100) : 0;
  return (
    <tr style={{ cursor: 'pointer' }} onClick={onOpen}>
      <td>
        <div style={{ fontWeight: 500 }}>{r.fedexInvoiceNumber ?? '—'}</div>
        <div className="muted" style={{ fontSize: 12 }}>{r.fileName}</div>
      </td>
      <td>{r.invoiceDate ? fmtUtcDate(r.invoiceDate) : '—'}</td>
      <td className="tabular" style={{ textAlign: 'right' }}>{fmtNum(r.shipmentCount)}</td>
      <td className="tabular" style={{ textAlign: 'right' }}>
        {fmtNum(r.matchedCount)} <span className="muted">({matchPct}%)</span>
      </td>
      <td className="tabular" style={{ textAlign: 'right' }}>{fmtCents(r.expressTotalCents)}</td>
      <td className="tabular" style={{ textAlign: 'right' }}>{fmtCents(r.grandTotalCents)}</td>
      <td>
        <StatusPill tone={r.status === 'reconciled' ? 'success' : 'staged'}>{r.status}</StatusPill>
      </td>
    </tr>
  );
}
